import { useEffect, useRef, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import SnackBar from "../../component/Snackbar/Snackbar";
import { useAuth } from "../../config/utilities/useAuth";
import Title from "../../component/title/Title";
import Calendar from "../../component/calendar/Calendar";
import SchedulingForm from "./SchedulingForm";
import {
  formatToYYYYMMDD,
  formatWithOrdinal,
} from "../../config/utilities/fDate";
import DailyView from "./DailyView";
import "./Scheduling.css";
import {
  APICreateSchedule,
  APIDeleteSchedule,
  APIGetSchedules,
  APIUpdateSchedule,
} from "../../config/utilities/APIs/schedulingApi";

const EMPTY_ATTENDANCE_SUMMARY = {
  Scheduled: 0,
  Exempted: 0,
  pickUp: 0,
  dropOff: 0,
  failedToBoard: 0,
};

export default function Scheduling() {
  const { privilege, tripTypes, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedData, setSelectedData] = useState(null);
  const [fetchedScheduleData, setFetchedScheduleData] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const isTripTypesReady = tripTypes?.length > 0;

  const initStatusFilterModeRef = useRef(null);

  useEffect(() => {
    if (!isTripTypesReady) return;

    const nextMode = isMobileViewport ? "mobile" : "desktop";
    if (initStatusFilterModeRef.current === nextMode) return;

    if (isMobileViewport) {
      setSelectedStatusFilter([]);
    } else {
      const stored = localStorage.getItem("calendar-selectedStatusFilter");
      setSelectedStatusFilter(stored !== null ? JSON.parse(stored) : []);
    }

    initStatusFilterModeRef.current = nextMode;
  }, [isTripTypesReady, tripTypes, isMobileViewport]);

  useEffect(() => {
    if (!isTripTypesReady) return;

    localStorage.setItem(
      "calendar-selectedStatusFilter",
      JSON.stringify(selectedStatusFilter)
    );
  }, [selectedStatusFilter, isTripTypesReady]);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const dailySummaryRequestIdRef = useRef(0);
  const [visibleRange, setVisibleRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [input, setInput] = useState({
    trip: "",
    driver: "",
    assistant: "",
    vehicle: "",
    dateType: "",
    from: "",
    to: "",
    includeWeekends: true,
    attendance: [],
  });

  const [error, setError] = useState({
    trip: false,
    driver: false,
    assistant: false,
    vehicle: false,
    dateType: false,
    from: false,
    to: false,
    attendance: false,
  });

  const handleReset = async () => {
    setInput({
      trip: "",
      driver: "",
      assistant: "",
      vehicle: "",
      dateType: "",
      from: "",
      to: "",
      includeWeekends: true,
      attendance: [],
    });
    setError({
      trip: false,
      driver: false,
      assistant: false,
      vehicle: false,
      dateType: false,
      from: false,
      to: false,
      attendance: false,
    });
    setFetchedScheduleData([]);
    setShowDeleteModal(false);
    setSelectedRowData(null);
    setAction(null);
    setSelectedDate(null);
    setSearchTerm("");
    await fetchSchedules(selectedStatusFilter, visibleRange);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    try {
      const response = await APIDeleteSchedule(row._id, setLoading, institutionId, token);

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Schedule has been deleted successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete schedule"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    setLoading(true);

    const formattedInput = formatInput(input, "edit", selectedRowData);

    try {
      const response = await APIUpdateSchedule(formattedInput, setLoading, institutionId, token);

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Scheduled trip has been updated successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update schedule"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  useEffect(() => {
    if (selectedDate) {
      setAction("daily");
    } else {
      setAction(null);
    }
  }, [selectedDate]);

  const fetchSchedules = async (type = selectedStatusFilter, visibleRange) => {
    setLoading(true);
    try {
      const response = await APIGetSchedules(
        {
          type: Array.isArray(type) && type.length > 0 ? type : undefined,
          visibleRange,
          institutionId,
        }
      );

      if (response.status === 200) {
        const { data } = response.data;

        if (!Array.isArray(data?.schedules)) {
          return;
        }

        setFetchedScheduleData(data.schedules);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isTripTypesReady || !institutionId) return;

    const storedView =
      JSON.parse(localStorage.getItem("calendar-view")) || "monthly";
    const effectiveView = isMobileViewport ? "monthly" : storedView;
    const now = new Date();
    let startDate, endDate;

    if (effectiveView === "monthly") {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (effectiveView === "weekly") {
      let dayOfWeek = now.getDay();
      if (dayOfWeek === 0) dayOfWeek = 7;
      startDate = new Date(now);
      startDate.setDate(now.getDate() - (dayOfWeek - 1));
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    }

    setVisibleRange({
      startDate: formatToYYYYMMDD(startDate),
      endDate: formatToYYYYMMDD(endDate),
    });
  }, [isTripTypesReady, institutionId, isMobileViewport]);

  useEffect(() => {
    if (
      !isTripTypesReady ||
      !institutionId ||
      !visibleRange.startDate ||
      !visibleRange.endDate
    ) {
      return;
    }

    fetchSchedules(selectedStatusFilter, visibleRange);
  }, [selectedStatusFilter, visibleRange, isTripTypesReady, institutionId]);

  const handleRangeChange = (date, view) => {
    let startDate, endDate;

    if (view === "monthly") {
      startDate = new Date(date.getFullYear(), date.getMonth(), 1);
      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    } else if (view === "weekly") {
      let dayOfWeek = date.getDay();
      if (dayOfWeek === 0) dayOfWeek = 7;
      startDate = new Date(date);
      startDate.setDate(date.getDate() - (dayOfWeek - 1));
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    }

    setVisibleRange({
      startDate: formatToYYYYMMDD(startDate),
      endDate: formatToYYYYMMDD(endDate),
    });
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = formatInput(input, "create", {});

    try {
      const response = await APICreateSchedule(
        formattedInput,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Trips have been successfully scheduled");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to schedule trips"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const formatInput = (input, mode, selectedRowData) => {
    const buildDates = () => {
      const from = input.from ? new Date(input.from) : null;
      if (!from || Number.isNaN(from.getTime())) return [];

      const to = input.dateType === "custom" && input.to ? new Date(input.to) : new Date(input.from);
      if (!to || Number.isNaN(to.getTime())) return [];

      const start = new Date(from);
      const end = new Date(to);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const dates = [];
      const cursor = new Date(start);
      while (cursor <= end) {
        const day = cursor.getDay();
        const isWeekend = day === 0 || day === 6;
        if (input.includeWeekends || !isWeekend) {
          dates.push(formatToYYYYMMDD(cursor));
        }
        cursor.setDate(cursor.getDate() + 1);
      }
      return dates;
    };

    const formatted = {
      vehicleId: input.vehicle,
      driverId: input.driver,
      ...(input.assistant &&
      input.assistant !== "" &&
      input.assistant !== "noDriver"
        ? { assistantId: input.assistant }
        : {}),
    };

    if (mode === "create") {
      const dates = buildDates();
      if (dates.length > 0) {
        formatted.dates = dates;
      }
      formatted.tripId = input.trip;
    } else {
      formatted.scheduleId = selectedRowData._id;
      if (Array.isArray(input.attendance) && input.attendance.length > 0) {
        formatted.exemptedArray = input.attendance;
      }
    }

    return formatted;
  };

  const getSummaryFromAttendance = (attendance) => {
    const summary = { ...EMPTY_ATTENDANCE_SUMMARY };
    const students = attendance?.students || [];

    students.forEach((student) => {
      const status = student?.status;
      if (status && Object.prototype.hasOwnProperty.call(summary, status)) {
        summary[status] += 1;
      }
    });

    return summary;
  };

  const handleSelectDate = async (selectedDate) => {
    setSelectedDate(selectedDate);

    const requestId = ++dailySummaryRequestIdRef.current;

    // Optimistic paint: show the day's rows from the already-loaded calendar
    // data immediately, then swap in the authoritative selectedDate response.
    // Compare YYYY-MM-DD strings (scheduleDate is already local-day based) to
    // avoid new Date("YYYY-MM-DD") parsing as UTC midnight and shifting the day
    // for users in negative UTC offsets.
    const selectedDateKey = formatToYYYYMMDD(selectedDate);
    const cachedRows = fetchedScheduleData
      .filter(
        (item) => String(item.scheduleDate).slice(0, 10) === selectedDateKey
      )
      .sort((a, b) => {
        const [aH, aM] = a.startTime ? a.startTime.split(":").map(Number) : [0, 0];
        const [bH, bM] = b.startTime ? b.startTime.split(":").map(Number) : [0, 0];
        return aH * 60 + aM - (bH * 60 + bM);
      });

    setSelectedData(cachedRows);
    setLoading(true);

    try {
      const response = await APIGetSchedules({
        selectedDate: formatToYYYYMMDD(selectedDate),
        type: selectedStatusFilter,
        institutionId,
      });

      // Ignore stale responses if the user changes selected date quickly.
      if (requestId !== dailySummaryRequestIdRef.current) return;

      // APIGetSchedules swallows errors and returns the Error object, so
      // surface it here to trigger the catch block and proper logging.
      if (response instanceof Error) {
        throw response;
      }

      if (response?.status === 200) {
        const schedules = response?.data?.data?.schedules;
        const rows = Array.isArray(schedules) ? schedules : [];

        const withSummary = rows
          .slice()
          .sort((a, b) => {
            const [aH, aM] = a.startTime ? a.startTime.split(":").map(Number) : [0, 0];
            const [bH, bM] = b.startTime ? b.startTime.split(":").map(Number) : [0, 0];
            return aH * 60 + aM - (bH * 60 + bM);
          })
          .map((row) => ({
            ...row,
            attendanceSummary: getSummaryFromAttendance(row),
          }));

        setSelectedData(withSummary);
      } else {
        setSelectedData([]);
      }
    } catch (error) {
      if (requestId !== dailySummaryRequestIdRef.current) return;
      console.error("Error fetching daily schedules:", error);
      setSelectedData([]);
    } finally {
      if (requestId === dailySummaryRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const getFormattedSelectedScheduleDate = () => {
    const dateValue = selectedRowData?.scheduleDate;
    if (!dateValue) return null;
    // Parse YYYY-MM-DD as local date to avoid UTC offset shifting the day.
    const ymdMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = ymdMatch
      ? new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]))
      : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return formatWithOrdinal(date);
  };

  const getScheduleViewTitle = () => {
    const tripName =
      selectedRowData?.tripDetails?.tripName?.trim() || "Schedule";
    const formattedDate = getFormattedSelectedScheduleDate();
    return formattedDate ? `${tripName} - ${formattedDate}` : tripName;
  };

  const renderScheduleDateBadge = () => {
    const formattedDate = getFormattedSelectedScheduleDate();
    if (!formattedDate) return null;
    return (
      <div className="schedule-date-badge">
        <span className="schedule-date-badge-value">
          {formattedDate}
        </span>
      </div>
    );
  };

  const renderScheduleViewTitle = () => {
    const title = getScheduleViewTitle();
    if (!title) return null;
    return (
      <div className="schedule-date-badge schedule-view-title">
        <span className="schedule-date-badge-value">{title}</span>
      </div>
    );
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <>
            <Title
              title="Edit Schedule"
              titleSize="1.5rem"
              gap="0.5rem"
              lineHeight="2.5rem"
            />
            {renderScheduleDateBadge()}
          </>
        );
      case "create":
        return (
          <Title
            title="Create Schedule"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        return (
          <Title
            title={getScheduleViewTitle()}
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "daily":
        return (
          <Title
            title={`${formatWithOrdinal(selectedDate)} Schedule`}
            subtitle="View all trips scheduled for this day"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );

      default:
        return (
          <Title
            title="Scheduling Module"
            subtitle="Manage all trips which are currently scheduled"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "create":
        return (
          <SchedulingForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
            setAction={setAction}
          />
        );
      case "edit":
        return (
          <SchedulingForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
            setAction={setAction}
          />
        );
      case "view":
        return (
          <SchedulingForm
            mode="view"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
            setAction={setAction}
          />
        );
      case "daily":
        return (
          <DailyView
            tableData={selectedData}
            handleDelete={handleDelete}
            loading={loading}
            setAction={setAction}
            showDeleteModal={showDeleteModal}
            setShowDeleteModal={setShowDeleteModal}
            selectedRowData={selectedRowData}
            setSelectedRowData={setSelectedRowData}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedDate={selectedDate}
            tableHeight={
              isMobileViewport
                ? "calc(100dvh - 3.5rem)"
                : "75vh"
            }
          />
        );
      default:
        return (
          <Calendar
            setAction={setAction}
            onSelectDate={handleSelectDate}
            data={fetchedScheduleData}
            selectedStatusFilter={selectedStatusFilter}
            setSelectedStatusFilter={setSelectedStatusFilter}
            loading={loading}
            onRangeChange={handleRangeChange}
            height={isMobileViewport ? "100%" : "47rem"}
          />
        );
    }
  };

  return (
    <div className="main-page scheduling-page">
      <NavBar type="scheduling" action={action} handleReset={(action === "create" || action === "daily") ? handleReset : () => setAction("daily")} />
      <div className="content-area">
        <div className="title-container mobile-hide-page-title">
          {renderTitle()}
        </div>
        {(action === "edit" || action === "view") && (
          <div className="scheduling-mobile-date-row">
            {action === "view"
              ? renderScheduleViewTitle()
              : renderScheduleDateBadge()}
          </div>
        )}
        <div
          className={`main-content-container scheduling-main-content-container ${
            action === null || action === "daily" ? "scheduling-full-height" : ""
          }`}
        >
          {renderContent()}
        </div>
      </div>
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
